import React, { useState, useEffect } from 'react';
import { format, addMonths, addWeeks, addDays, addYears, parse, isBefore, isAfter, parseISO, isEqual, startOfDay } from 'date-fns';
import ProgressBar from './ProgressBar';
import ToPeso from './ToPeso';
import './css/DueDates.scss';

// Function to round up to the nearest cent if the second decimal place is not zero
const roundUp = (amount) => {
  const amountString = amount.toFixed(2);
  const secondDecimalPlace = amountString.charAt(amountString.length - 1);

  if (secondDecimalPlace !== '0') {
    return parseFloat((amount + 0.01).toFixed(2));
  }

  return amount;
};

const calculateDueDates = (startDate, interval, term, amount, interest, adv) => {
  const dueDates = [];
  const principalAmount = amount / term;
  const interestAmountInitial = (adv)?((amount * interest) / 100):0;
  const interestAmountMinusAdvance = (((amount * interest) / 100) * term) - interestAmountInitial;
  const interestAmount = interestAmountMinusAdvance / term;

  let currentDate = new Date(startDate);

  for (let i = 0; i < term; i++) {
    switch (interval.toLowerCase()) {
      case 'monthly':
        currentDate = addDays(currentDate, 31);
        break;
      case 'bimonthly': // Twice a month, roughly every two weeks
        currentDate = addWeeks(currentDate, 2);
        break;
      case 'weekly':
        currentDate = addWeeks(currentDate, 1);
        break;
      case 'quarterly':
        currentDate = addMonths(currentDate, 3);
        break;
      case 'semiannual':
        currentDate = addMonths(currentDate, 6);
        break;
      case 'yearly':
        currentDate = addYears(currentDate, 1);
        break;
      default:
        throw new Error('Invalid interval');
    }

    dueDates.push({
      date: format(currentDate, 'MM/dd/yyyy'),
      dueAmount: roundUp(principalAmount + interestAmount),
      actualDueAmount: parseFloat(principalAmount + interestAmount)
    });
  }

  return dueDates;
};

const checkPayments = (startDate, dueDate, previousDueDate, paymentList, dueAmount, isLastDueDate) => {
  let totalPaid = 0;
  let status = 'nopayment';
  let amountDifference = 0;

  // Parse dates and set all to midnight
  const startDateParsed = startOfDay(
    previousDueDate 
      ? addDays(parse(previousDueDate, 'MM/dd/yyyy', new Date()), 1)
      : new Date(startDate)
  );
  const endDateParsed = startOfDay(parse(dueDate, 'MM/dd/yyyy', new Date()));
  const currentDate = startOfDay(new Date());

  // Get payments within the date range
  const paymentsInRange = paymentList.filter(payment => {
    const paymentDate = startOfDay(parseISO(payment.paymentDate));
    // Include payments on the start and end dates
    return (isAfter(paymentDate, startDateParsed) || isEqual(paymentDate, startDateParsed)) && 
           (isBefore(paymentDate, endDateParsed) || isEqual(paymentDate, endDateParsed));
  });

  // Calculate total payments for this period
  totalPaid = paymentsInRange.reduce((sum, payment) => 
    sum + (
      parseFloat(payment.paidPrincipal || 0) + 
      parseFloat(payment.paidInterest || 0) + 
      parseFloat(payment.paidOther || 0)
    ), 0);

  // Determine status based on payments
  if (paymentsInRange.length > 0) {
    if (totalPaid >= dueAmount) {
      status = 'paid';
      amountDifference = totalPaid - dueAmount;
    } else {
      status = 'lacking';
      amountDifference = dueAmount - totalPaid;
    }
  } else if (isAfter(currentDate, endDateParsed)) {
    status = 'missed';
    amountDifference = dueAmount;
  }
  
  return { 
    status, 
    amountDifference, 
    totalPaid
  };
};

const DueDates = ({ date, interval, term, amount, interest, paymentList, loanItem }) => {
  const dueDates = calculateDueDates(date, interval, term, amount, interest, loanItem.advanceInterest);
  const initialIntrest = (loanItem.advanceInterest)?(amount * (interest/100)):0;
  const totalRepayments = dueDates.reduce((acc, due) => acc + due.actualDueAmount, 0) + initialIntrest;

  const [totalPaid, setTotalPaid] = useState(0);
  const [remainingBalance, setRemainingBalance] = useState(totalRepayments);
  const [totalPenalty, setTotalPenalty] = useState(0); // Add new state

  useEffect(() => {
    let total = 0;
    let penaltyTotal = 0; // Add penalty tracker

    paymentList.forEach(payment => {
      total += (
        parseFloat(payment.paidPrincipal || 0) + 
        parseFloat(payment.paidInterest || 0) + 
        parseFloat(payment.paidOther || 0)
      );
      penaltyTotal += parseFloat(payment.paidPenalty || 0); // Sum penalties
    });
    
    setTotalPaid(total + initialIntrest);
    setTotalPenalty(penaltyTotal);
    setRemainingBalance(totalRepayments - total - initialIntrest);
  }, [paymentList, totalRepayments, initialIntrest]);

  return (
    <>
      <div id="payment-duedates">
        <ul>
          {dueDates.map((due, index) => {
            const previousDueDate = index > 0 ? dueDates[index - 1].date : null;
            const isLastDueDate = index === dueDates.length - 1;
            const { status, amountDifference, totalPaid } = checkPayments(date, due.date, previousDueDate, paymentList, due.dueAmount, isLastDueDate);
            
            // Mapping status to text status
            const statusTextMap = {
              'paid': 'Paid',
              'lacking': 'Lacking',
              'missed': 'Missed',
              'default': 'No Payment'
            };

            const txtstatus = statusTextMap[status] || statusTextMap['default'];

            return (
              <li key={index} className={status}>
                <span className="term">
                  <div>{index + 1}</div>
                </span>
                <span className="txtdate">{due.date}</span> (
                <b>
                  <ToPeso amount={due.dueAmount} />
                </b>
                ) <span className={`${status} status`}>{txtstatus}</span>
                {status === 'lacking' && (
                  <>
                    {' '} <span className="lack"><i className="fa-regular fa-circle-down"></i> <ToPeso amount={amountDifference} /></span> <span className="actual"><i className="fa-solid fa-hand-holding-dollar"></i> <ToPeso amount={totalPaid} /></span>
                  </>
                )}
                {status === 'paid' && amountDifference > 0 && (
                  <>
                    {' '} <span className="excess"><i className="fa-regular fa-circle-up"></i> <ToPeso amount={amountDifference} /></span> <span className="actual"><i className="fa-solid fa-hand-holding-dollar"></i> <ToPeso amount={totalPaid} /></span>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      <ProgressBar 
        totalPayments={parseFloat(totalPaid).toFixed(2)} 
        remainingBalance={parseFloat(remainingBalance).toFixed(2)} 
        totalRepayments={parseFloat(totalRepayments).toFixed(2)}
        totalPenalty={parseFloat(totalPenalty).toFixed(2)} // Add total penalty
      />
    </>
  );
};

export default DueDates;
